import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import LayoutOneColumnHeader from "@krea/customer-features/shared-components/app/one-column/LayoutOneColumnHeader";
import ProgressBarTriage from "@krea/customer-features/shared-components/app/progress-bar/ProgressBarTriage";
import Button from "@krea/customer-features/shared-components/button";
import { FieldRequestForm } from "@krea/customer-features/shared-components/field-request/GetFieldRequestForm";
import { LoanApplicationFormWrapper } from "@krea/customer-features/shared-components/loan-application-form-wrapper";
import { ToastAdditionalContent } from "@krea/customer-features/shared-components/toast/ToastAdditionalContent";
import { TriageAlert } from "@krea/customer-features/shared-components/triage/triage-alert";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";
import {
  FieldRequestFieldEnum,
  IFieldRequest,
} from "@krea/customer-features/types/fieldRequest";
import {
  getDefaultToastProperties,
  getDefaultToastStates,
  getNextRequest,
} from "@krea/customer-features/utils";

import { useGetCurrentLoanApplication } from "store/current-loan-application/hooks/queries";

import { FieldRequestStepNavigation } from "./FieldRequestStepNavigation";

interface IApplicationQuestionsStepProps {
  step: IFieldRequest;
  steps: IFieldRequest[];
}

export const ApplicationQuestionsStep = ({
  step,
  steps,
}: IApplicationQuestionsStepProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useGetCurrentLoanApplication();
  const triageFields = data?.triageFields ?? [];
  const { data: fieldRequestData } = useGetOneFieldRequest(step.id);
  const isFieldRequestSubmitted = fieldRequestData?.isSubmitted ?? false;

  const nextFieldRequest = getNextRequest(triageFields, steps, step.field);

  const toastProperties = getDefaultToastProperties(steps);
  const toastStates = getDefaultToastStates();

  const [isToastOpen, setIsToastOpen] =
    useState<Record<string, { isOpen: boolean } | undefined>>(toastStates);

  const currentToast = useMemo(
    () => ({
      ...toastProperties[step.field],
      isOpen: isToastOpen[step.field]?.isOpen,
    }),
    [toastProperties, isToastOpen, step.field],
  );

  const closeToast = () => {
    setIsToastOpen((prev) => ({ ...prev, [step.field]: { isOpen: false } }));
  };

  const goToNextStep = () => {
    if (nextFieldRequest) {
      navigate(
        `/direct/improve-application-questions/${nextFieldRequest.field}`,
      );
    }
  };

  return (
    <LoanApplicationFormWrapper>
      <ProgressBarTriage steps={steps} />
      {currentToast.isOpen && (
        <ToastAdditionalContent
          isWarning={currentToast.warning}
          innerText={{
            title: currentToast.title,
            subtitle: currentToast.subtitle,
          }}
          className="tw-mb-7"
          {...(!currentToast.skippable &&
            currentToast.closable && { onClick: closeToast })}
          additional={
            currentToast.skippable && (
              <Button onClick={goToNextStep} variant="text">
                {isFieldRequestSubmitted
                  ? t("iaq.nextQuestionCta")
                  : t("iaq.skipQuestionCta")}
              </Button>
            )
          }
        />
      )}

      <LayoutOneColumnHeader isStepPage step={step} />

      <TriageAlert
        field={
          step.field.replace(
            /[A-Z]/g,
            (letter) => `_${letter.toLowerCase()}`,
          ) as FieldRequestFieldEnum
        }
      />
      <div className="tw-mb-12">
        <FieldRequestStepNavigation
          title={step.displayName}
          field={step.field}
          fieldRequestID={step.id}
          preview={false}
        >
          <FieldRequestForm fieldRequestID={step.id} />
        </FieldRequestStepNavigation>
      </div>
    </LoanApplicationFormWrapper>
  );
};
